import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { heroData } from '../data/siteData';

const CinematicHero = ({ videoUrl }) => {
  const videoRef = useRef(null);
  const [phase, setPhase] = useState('video'); // 'video' | 'transition' | 'hero'
  const [grayscale, setGrayscale] = useState(0);
  const [heroReady, setHeroReady] = useState(false);
  const [fastReveal, setFastReveal] = useState(false); // Track if triggered by scroll for faster animations

  // Typewriter state
  const words = heroData.headline.split(' ');
  const [typedCount, setTypedCount] = useState(0);
  const typedRef = useRef(null);

  // Track video progress → trigger grayscale + hero near end
  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.duration) return;

    const remaining = v.duration - v.currentTime;

    // Last 3 seconds: start grayscale transition
    if (remaining <= 3) {
      const g = Math.min(1, Math.max(0, 1 - (remaining / 3)));
      setGrayscale(g);

      if (phase === 'video') {
        setPhase('transition');
      }
    }

    // Last 0.3 seconds: lock hero on
    if (remaining <= 0.3 && phase !== 'hero') {
      setGrayscale(1);
      setPhase('hero');
      setHeroReady(true);
    }
  }, [phase]);

  // When video ends (fallback — loop may prevent this)
  const handleVideoEnded = useCallback(() => {
    setGrayscale(1);
    setPhase('hero');
    setHeroReady(true);
  }, []);

  // Start typewriter once hero is ready
  useEffect(() => {
    if (!heroReady) return;

    // Faster typewriter speed when triggered by scroll
    const interval = fastReveal ? 60 : 120;
    
    typedRef.current = setInterval(() => {
      setTypedCount(prev => {
        if (prev >= words.length) {
          clearInterval(typedRef.current);
          return prev;
        }
        return prev + 1;
      });
    }, interval);

    return () => clearInterval(typedRef.current);
  }, [heroReady, words.length, fastReveal]);

  // Auto-show hero content after 1200ms
  useEffect(() => {
    const timer = setTimeout(() => {
      if (phase !== 'hero') {
        setGrayscale(1);
        setPhase('hero');
        setHeroReady(true);
      }
    }, 1200);

    return () => clearTimeout(timer);
  }, [phase]);

  // Block scroll during fast reveal transition
  const [scrollLocked, setScrollLocked] = useState(false);
  
  // Lock scroll initially while in video phase - prevent any scroll before hero shows
  useEffect(() => {
    if (phase === 'video') {
      document.body.style.overflow = 'hidden';
      return () => {
        if (phase === 'video') {
          document.body.style.overflow = '';
        }
      };
    }
  }, [phase]);
  
  useEffect(() => {
    if (scrollLocked) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Lock scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      
      // Unlock after hero content renders (fast reveal takes ~400ms total)
      const unlockTimer = setTimeout(() => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        // Restore scroll position
        window.scrollTo(0, scrollY);
        setScrollLocked(false);
      }, 500);
      
      return () => {
        clearTimeout(unlockTimer);
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
      };
    }
  }, [scrollLocked]);
  
  // Unlock scroll when hero phase starts (from timer, not scroll)
  useEffect(() => {
    if (phase === 'hero' && !scrollLocked) {
      document.body.style.overflow = '';
    }
  }, [phase, scrollLocked]);

  // Allow skipping the intro on scroll - trigger immediately on any scroll
  useEffect(() => {
    if (phase === 'hero') return; // Don't add listeners after hero is shown
    
    const triggerFastReveal = (e) => {
      if (phase !== 'hero') {
        if (e) e.preventDefault();
        setScrollLocked(true); // Lock scroll during transition
        setFastReveal(true);
        setGrayscale(1);
        setPhase('hero');
        setHeroReady(true);
        // Force scroll to top
        window.scrollTo(0, 0);
      }
    };
    
    // Wheel event - prevent default and trigger reveal
    const handleWheel = (e) => {
      if (phase !== 'hero' && Math.abs(e.deltaY) > 0) {
        e.preventDefault();
        e.stopPropagation();
        triggerFastReveal();
      }
    };
    
    // Touch move for mobile
    let touchStartY = 0;
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e) => {
      if (phase !== 'hero') {
        const touchY = e.touches[0].clientY;
        const deltaY = touchStartY - touchY;
        if (Math.abs(deltaY) > 10) { // Minimum swipe threshold
          e.preventDefault();
          e.stopPropagation();
          triggerFastReveal();
        }
      }
    };
    
    // Keyboard scroll (spacebar, arrow keys, page down)
    const handleKeyDown = (e) => {
      if (phase !== 'hero' && ['Space', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp'].includes(e.code)) {
        e.preventDefault();
        triggerFastReveal();
      }
    };
    
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [phase]);

  // Also allow click-to-skip
  const skipToHero = () => {
    if (phase !== 'hero') {
      setGrayscale(1);
      setPhase('hero');
      setHeroReady(true);
    }
  };

  const showHeroContent = phase === 'hero';

  return (
    <section className="relative min-h-screen" data-testid="cinematic-hero">
      {/* Video Layer — fixed behind everything */}
      <div className="fixed inset-0 z-0">
        {videoUrl ? (
          <video
            ref={videoRef}
            className="w-full h-full object-cover transition-[filter] duration-1000"
            style={{
              filter: `grayscale(${showHeroContent ? 1 : grayscale}) brightness(${showHeroContent ? 0.5 : 1 - grayscale * 0.3})`,
            }}
            src={videoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        ) : (
          <div className="w-full h-full bg-[#111]" />
        )}

        {/* Dark overlay — dims video for text readability but keeps it visible */}
        <div
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: showHeroContent ? 0.6 : 0, background: 'linear-gradient(to bottom, rgba(10,10,10,0.7), rgba(10,10,10,0.5))' }}
        />
      </div>

      {/* Skip hint during video phase */}
      <AnimatePresence>
        {phase === 'video' && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 2, duration: 0.5 }}
            onClick={skipToHero}
            className="fixed bottom-8 right-8 z-30 text-xs font-mono tracking-wider text-white/40 border border-white/20 px-4 py-2 rounded-full hover:text-white/70 hover:border-white/40 transition-colors"
            data-testid="skip-intro"
          >
            Skip Intro
          </motion.button>
        )}
      </AnimatePresence>

      {/* Hero Content — appears after video transition */}
      <div className="relative z-10 min-h-screen flex items-center pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <AnimatePresence>
            {showHeroContent && (
              <>
                {/* Headline */}
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight max-w-5xl mb-8" style={{ lineHeight: 1.15, paddingBottom: '0.05em' }}>
                  {words.map((word, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, y: fastReveal ? 6 : 12 }}
                      animate={i < typedCount ? { opacity: 1, y: 0 } : { opacity: 0.08, y: 0 }}
                      transition={{ duration: fastReveal ? 0.1 : 0.25, ease: 'easeOut' }}
                      className={`inline-block mr-[0.3em] ${
                        (word === 'Diverse' || word === 'Physical' || word === 'AI') ? 'gradient-text' : ''
                      }`}
                      style={{ paddingBottom: '0.05em' }}
                    >
                      {word}
                    </motion.span>
                  ))}
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, repeatType: 'reverse' }}
                    className="inline-block w-[3px] h-[0.8em] bg-[#FF7A00] ml-1"
                  />
                </h1>

                {/* Subheadline */}
                <motion.p
                  initial={{ opacity: 0, y: fastReveal ? 10 : 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: fastReveal ? 0.15 : 0.6, delay: fastReveal ? 0.1 : 0.8 }}
                  className="text-base lg:text-lg text-white/80 max-w-2xl leading-relaxed mb-12 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
                >
                  {heroData.subheadline}
                </motion.p>

                {/* CTAs */}
                <motion.div
                  initial={{ opacity: 0, y: fastReveal ? 10 : 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: fastReveal ? 0.15 : 0.5, delay: fastReveal ? 0.15 : 1.0 }}
                  className="flex flex-wrap gap-4 mb-20"
                >
                  <Link
                    to="/contact"
                    className="group flex items-center gap-2 btn-gradient px-7 py-3.5 font-semibold text-sm rounded-full"
                    data-testid="hero-cta-contact"
                  >
                    Request Data
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                  <Link
                    to="/data/showcase"
                    className="flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white font-medium text-sm rounded-full hover:border-[#FF7A00]/40 hover:text-[#FF7A00] transition-colors backdrop-blur-sm"
                    data-testid="hero-cta-datasets"
                  >
                    Explore the Data Showcase
                  </Link>
                </motion.div>

                {/* Stats Row */}
                <motion.div
                  initial={{ opacity: 0, y: fastReveal ? 15 : 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: fastReveal ? 0.2 : 0.7, delay: fastReveal ? 0.2 : 1.2 }}
                  className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-white/15 rounded-xl overflow-hidden backdrop-blur-md bg-black/50"
                >
                  {heroData.stats.map((s, i) => (
                    <div
                      key={i}
                      className="p-6 lg:p-8"
                      style={{ borderRight: i < heroData.stats.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}
                    >
                      <div className="font-mono text-2xl lg:text-3xl font-bold gradient-text">{s.value}</div>
                      <div className="text-xs font-mono tracking-[0.15em] uppercase text-white/60 mt-1">{s.label}</div>
                    </div>
                  ))}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default CinematicHero;
